import React, { useEffect, useState, useMemo, useRef } from 'react';
import Sidebar from '../../components/Sidebar';
import axios from 'axios';
import { Upload, Trash2, Search, ImageOff, CheckCircle2 } from 'lucide-react';
import { products, brands } from '../../data/products';
import { getCategoryIcon, getBrandLogo, getBrandGradient } from '../../utils/productAssets';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_KB = 600;

interface ImageMap { [productId: string]: string; }

export default function AdminProducts() {
  const [images, setImages] = useState<ImageMap>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('All');
  const [coverageOnly, setCoverageOnly] = useState<'all' | 'with' | 'without'>('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/products/images`);
      setImages(data || {});
    } catch {} finally { setLoading(false); }
  };

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const handleFile = async (productId: string, file: File) => {
    if (!file.type.startsWith('image/')) { showToast('Please select an image file', false); return; }
    if (file.size > MAX_KB * 1024) { showToast(`Image too large (${Math.round(file.size / 1024)} KB). Max ${MAX_KB} KB.`, false); return; }
    setBusyId(productId);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(r.error);
        r.readAsDataURL(file);
      });
      await axios.put(`${API}/api/admin/products/${productId}/image`, { image_url: dataUrl });
      setImages(prev => ({ ...prev, [productId]: dataUrl }));
      showToast('Image uploaded', true);
    } catch (e: any) {
      showToast(e.response?.data?.detail || 'Upload failed', false);
    } finally { setBusyId(null); }
  };

  const handleDelete = async (productId: string) => {
    if (!window.confirm('Remove this product image?')) return;
    setBusyId(productId);
    try {
      await axios.delete(`${API}/api/admin/products/${productId}/image`);
      setImages(prev => { const cp = { ...prev }; delete cp[productId]; return cp; });
      showToast('Image removed', true);
    } catch { showToast('Delete failed', false); } finally { setBusyId(null); }
  };

  const filtered = useMemo(() => {
    return products.filter(p => {
      if (brandFilter !== 'All' && p.brand !== brandFilter) return false;
      if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.category.toLowerCase().includes(search.toLowerCase())) return false;
      const has = !!images[p.id];
      if (coverageOnly === 'with' && !has) return false;
      if (coverageOnly === 'without' && has) return false;
      return true;
    });
  }, [search, brandFilter, coverageOnly, images]);

  const coverage = Math.round((Object.keys(images).length / products.length) * 100);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-section)' }}>
      <Sidebar type="admin" active="products" />
      <main style={{ flex: 1, padding: '32px 40px' }} data-testid="admin-products-page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 8, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--secondary)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 4 }}>MARKETPLACE</p>
            <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>Product Images</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 4 }}>Upload a custom image for each product. Max {MAX_KB} KB. PNG/JPG/WEBP/SVG.</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: 'white', border: '1px solid var(--border)', borderRadius: 4 }}>
            <CheckCircle2 size={18} color="var(--primary)" />
            <div>
              <div style={{ fontWeight: 700, fontSize: 18, lineHeight: 1 }} data-testid="coverage-percent">{coverage}%</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Coverage ({Object.keys(images).length}/{products.length})</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 24, marginBottom: 20 }}>
          <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 320 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input data-testid="product-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products…"
              style={{ width: '100%', padding: '9px 12px 9px 30px', border: '1px solid var(--border)', fontSize: 13, background: 'white' }} />
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {brands.map(b => (
              <button key={b} data-testid={`filter-brand-${b}`} onClick={() => setBrandFilter(b)}
                style={{ padding: '8px 14px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
                  background: brandFilter === b ? '#0A1128' : 'white', color: brandFilter === b ? 'white' : 'var(--text-secondary)',
                  border: '1px solid ' + (brandFilter === b ? '#0A1128' : 'var(--border)') }}>{b}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {(['all', 'with', 'without'] as const).map(c => (
              <button key={c} data-testid={`filter-coverage-${c}`} onClick={() => setCoverageOnly(c)}
                style={{ padding: '8px 14px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
                  background: coverageOnly === c ? 'var(--primary)' : 'white', color: coverageOnly === c ? 'white' : 'var(--text-secondary)',
                  border: '1px solid ' + (coverageOnly === c ? 'var(--primary)' : 'var(--border)') }}>
                {c === 'all' ? 'All' : c === 'with' ? 'With image' : 'Missing image'}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {filtered.map(p => {
              const Icon = getCategoryIcon(p.category);
              const img = images[p.id];
              const isBusy = busyId === p.id;
              return (
                <div key={p.id} data-testid={`admin-product-${p.id}`}
                  style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {/* Preview */}
                  <div style={{ height: 140, background: getBrandGradient(p.brand), position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {img ? (
                      <img src={img} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'contain', background: 'white' }} />
                    ) : (
                      <>
                        <div style={{ position: 'absolute', inset: 0, opacity: 0.08, backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '14px 14px' }} />
                        <img src={getBrandLogo(p.brand)} alt={p.brand} style={{ width: 60, height: 60, objectFit: 'contain', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))', position: 'relative', zIndex: 2 }} />
                        <div style={{ position: 'absolute', top: 10, right: 10, color: 'rgba(255,255,255,0.45)' }}><Icon size={20} strokeWidth={1.5} /></div>
                      </>
                    )}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: p.brandColor }} />
                    {!img && (
                      <span style={{ position: 'absolute', top: 8, left: 8, padding: '2px 7px', background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}>Default</span>
                    )}
                  </div>

                  <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, padding: '2px 6px', background: `${p.brandColor}15`, color: p.brandColor, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{p.brand}</span>
                      <span style={{ fontSize: 9, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{p.category}</span>
                    </div>
                    <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', minHeight: 32, lineHeight: 1.25, marginBottom: 10 }}>{p.name}</h3>

                    <input ref={el => { fileInputs.current[p.id] = el; }} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" style={{ display: 'none' }}
                      data-testid={`file-input-${p.id}`}
                      onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(p.id, f); e.target.value = ''; }} />
                    <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
                      <button data-testid={`upload-btn-${p.id}`} disabled={isBusy} onClick={() => fileInputs.current[p.id]?.click()}
                        style={{ flex: 1, padding: '8px', background: img ? 'white' : 'var(--primary)', color: img ? 'var(--primary)' : 'white', border: '1px solid var(--primary)', fontSize: 11, fontWeight: 700, cursor: isBusy ? 'wait' : 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <Upload size={12} /> {img ? 'Replace' : 'Upload'}
                      </button>
                      {img && (
                        <button data-testid={`delete-btn-${p.id}`} disabled={isBusy} onClick={() => handleDelete(p.id)}
                          style={{ padding: '8px 10px', background: 'white', color: '#D32F2F', border: '1px solid #D32F2F', fontSize: 11, fontWeight: 700, cursor: isBusy ? 'wait' : 'pointer' }}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {filtered.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
            <ImageOff size={36} style={{ opacity: 0.3, marginBottom: 8 }} />
            <p style={{ fontSize: 14 }}>No products match your filters.</p>
          </div>
        )}

        {toast && (
          <div data-testid="toast"
            style={{ position: 'fixed', bottom: 24, right: 24, padding: '12px 18px', background: toast.ok ? 'var(--primary)' : '#D32F2F', color: 'white', fontSize: 13, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', zIndex: 100 }}>
            {toast.msg}
          </div>
        )}
      </main>
    </div>
  );
}
